export type OverrideMergeStrategy = 'keyed' | 'prose' | 'derived' | 'frozen';

export type ReconcileOverrideParams = {
	/** the field's freshly generated HTML for the CURRENT model */
	generated: string;
	/** the stored manual override HTML */
	manual: string;
	/** the generated HTML the override was last edited against; undefined for a legacy override saved before base-capture */
	base?: string;
	strategy: OverrideMergeStrategy;
};

export type Segment = { html: string; text: string };

type KeyedEntry = { key: string | null; segment: Segment };

const decodeEntities = (value: string): string => value
	.replace(/&nbsp;/giu, ' ')
	.replace(/&lt;/giu, '<')
	.replace(/&gt;/giu, '>')
	.replace(/&quot;/giu, '"')
	.replace(/&#0*39;|&apos;/giu, '\'')
	.replace(/&#(?<code>\d+);/gu, (_match, code) => String.fromCharCode(parseInt(code, 10)))
	.replace(/&amp;/giu, '&');

/** Visible, normalized text of an HTML fragment — the identity used to align segments; markup and entity noise are stripped so serialization differences never read as content changes. */
const stripText = (html: string): string => decodeEntities(html.replace(/<[^>]*>/gu, '')).replace(/\s+/gu, ' ').trim();

/** Splits a field's HTML into its visible line segments, treating <br> and block boundaries as breaks. Only leading/trailing blank segments are dropped — an interior blank line is intentional spacing the user typed between notes and must survive the merge (GitHub #401). Leading whitespace on a segment is kept (never trimmed) -- it is how a user hand-aligns a row (GitHub #536); only trailing whitespace (serialization noise from the tag boundary, never meaningful on its own) is stripped. */
export const splitSegments = (html: string): Array<Segment> => {
	const parts = html
		.replace(/\r\n?/gu, '\n')
		.replace(/<\s*br\s*\/?\s*>/giu, '\n')
		.replace(/<\s*\/\s*(?:div|p)\s*>/giu, '\n')
		.replace(/<\s*(?:div|p)\b[^>]*>/giu, '\n')
		.split('\n')
		.map((part): Segment => ({ html: part.replace(/\s+$/u, ''), text: stripText(part) }));

	let start = 0;
	let end = parts.length;
	while (start < end && parts[start].text.length === 0) start++;
	while (end > start && parts[end - 1].text.length === 0) end--;
	return parts.slice(start, end);
};

const joinSegments = (segments: Array<Segment>): string => segments.map((segment) => segment.html).join('<br>');

/** The stable key for a keyed-list row: the SKU token before the first colon (SKUs never contain whitespace). Freehand lines (customer-supplied notes, prose) have no such token and return null so they are preserved, never reconciled away. */
const keyOf = (text: string): string | null => {
	const colon = text.indexOf(':');
	if (colon <= 0) return null;
	const prefix = text.slice(0, colon).trim();
	if (!prefix || /\s/u.test(prefix)) return null;
	return prefix;
};

/** Assigns each segment its occurrence-indexed key so duplicate SKUs stay distinct across the three versions. */
const keyedList = (segments: Array<Segment>): Array<KeyedEntry> => {
	const counts: Record<string, number> = {};
	return segments.map((segment) => {
		const token = keyOf(segment.text);
		if (token === null) return { key: null, segment };
		counts[token] = (counts[token] ?? 0) + 1;
		return { key: `${token}#${counts[token]}`, segment };
	});
};

const indexByKey = (segments: Array<Segment>): Map<string, Segment> => {
	const map = new Map<string, Segment>();
	keyedList(segments).forEach(({ key, segment }) => {
		if (key) map.set(key, segment);
	});
	return map;
};

/** Where a new row goes when no generated row before it survived into the output: ahead of the first later row that did, so freehand lines the user put above the list stay above it. */
const firstEmittedPosition = (gen: Array<KeyedEntry>, from: number, emitted: Set<string>, outKeys: Array<string | null>): number => {
	for (let i = from + 1; i < gen.length; i++) {
		const { key } = gen[i];
		if (!key || !emitted.has(key)) continue;
		const at = outKeys.indexOf(key);
		if (at !== -1) return at;
	}
	return outKeys.length;
};

/**
 * Row-keyed reconciliation for the appliance list. The user's arrangement (order, freehand lines)
 * is authoritative; automation flows through it: rows the model REMOVED are dropped, rows it added
 * are inserted at their generated position, and a row the model changed re-surfaces unless the user's
 * base proves the model left it untouched (then the manual edit is kept). Works with no base — every
 * keyed row still tracked by generated then reflects the current model, which is exactly the
 * retroactive fix for a frozen list.
 *
 * A "model removed it" row is only dropped when base proves it once existed as a real generated row
 * (i.e. it's in baseByKey but no longer in genByKey). A keyed-looking manual row that base never
 * tracked either (a hand-typed line such as "SOME-SKU: description" that was never a real generated
 * appliance, e.g. GitHub #446-adjacent bug where these vanished the moment the page next re-rendered
 * — typically on PDF/PPTX export, since typing itself doesn't trigger a re-render) is a pure manual
 * addition and is preserved, exactly like a freehand (non-keyed) line.
 *
 * Orphaned-row aliasing: a manual keyed row whose key is not in generated (the user edited the SKU
 * token itself — removed an `-NG`/`-LP` suffix, renamed a SKU) is position-aligned against base rows
 * whose keys are not in manual. When the base's key at the aligned position is still in generated,
 * the manual row is treated as an in-place edit of that base row rather than dropped-and-reappended
 * at the end.
 */
const reconcileKeyed = (params: ReconcileOverrideParams): string => {
	const { generated, manual, base } = params;
	const genSegs = splitSegments(generated);
	const manSegs = splitSegments(manual);
	const baseSegs = base === undefined ? [] : splitSegments(base);
	const gen = keyedList(genSegs);
	const man = keyedList(manSegs);
	const bas = keyedList(baseSegs);
	const genByKey = indexByKey(genSegs);
	const baseByKey = indexByKey(baseSegs);

	const manKeys = new Set(man.map(({ key }) => key).filter((k): k is string => k !== null));
	const orphanedManual = man
		.map((entry, index) => ({ entry, index }))
		.filter(({ entry }) => entry.key !== null && !genByKey.has(entry.key));
	const orphanedBaseKeys = bas
		.filter(({ key }) => key !== null && !manKeys.has(key))
		.map(({ key }) => key as string);
	const aliasKey = new Map<number, string>();
	orphanedManual.forEach(({ index }, position) => {
		const basKey = orphanedBaseKeys[position];
		if (basKey && genByKey.has(basKey)) aliasKey.set(index, basKey);
	});

	const emitted = new Set<string>();
	const out: Array<Segment> = [];
	const outKeys: Array<string | null> = [];

	man.forEach(({ key, segment }, index) => {
		if (key === null) {
			out.push(segment);
			outKeys.push(null);
			return;
		}
		const effectiveKey = aliasKey.get(index) ?? key;
		const genSegment = genByKey.get(effectiveKey);
		if (!genSegment) {
			// No matching generated row. Only drop it as stale when base proves it once WAS a real,
			// tracked row (the model deleted the underlying item) — a row base never tracked either is a
			// pure manual addition (freehand text that happens to look like "TOKEN: description", e.g. a
			// hand-typed appliance with no matching SKU) and must be preserved, never silently discarded.
			if (!baseByKey.has(effectiveKey)) {
				out.push(segment);
				outKeys.push(null);
			}
			return;
		}
		emitted.add(effectiveKey);
		const baseSegment = baseByKey.get(effectiveKey);
		out.push(baseSegment && baseSegment.text === genSegment.text ? segment : genSegment);
		outKeys.push(effectiveKey);
	});

	// A row the model ADDED lands where the model put it — directly after the row it follows in the
	// canonical order — instead of at the bottom, which is where appending left every new appliance.
	let cursor = -1;
	gen.forEach(({ key, segment }, index) => {
		if (!key) return;
		if (emitted.has(key)) {
			cursor = outKeys.indexOf(key);
			return;
		}
		const at = cursor >= 0 ? cursor + 1 : firstEmittedPosition(gen, index, emitted, outKeys);
		out.splice(at, 0, segment);
		outKeys.splice(at, 0, key);
		cursor = at;
	});

	return joinSegments(out);
};

/** Longest common subsequence of two text arrays, returned as aligned index pairs. */
const lcsPairs = (left: Array<string>, right: Array<string>): Array<[number, number]> => {
	const m = left.length;
	const n = right.length;
	const dp: Array<Array<number>> = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
	for (let i = m - 1; i >= 0; i--) {
		for (let j = n - 1; j >= 0; j--) {
			dp[i][j] = left[i] === right[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
		}
	}
	const pairs: Array<[number, number]> = [];
	let i = 0;
	let j = 0;
	while (i < m && j < n) {
		if (left[i] === right[j]) {
			pairs.push([i, j]);
			i++;
			j++;
		}
		else if (dp[i + 1][j] >= dp[i][j + 1]) i++;
		else j++;
	}
	return pairs;
};

const sliceText = (segments: Array<Segment>): string => segments.map((segment) => segment.text).join(' ');

const alignmentMap = (oText: Array<string>, other: Array<string>): Map<number, number> => {
	const map = new Map<number, number>();
	lcsPairs(oText, other).forEach(([o, x]) => map.set(o, x));
	return map;
};

/**
 * Resolves one changed region between two stable anchors. Non-conflicting changes take whichever
 * side moved; a true two-sided conflict prefers the manual edit (deliberate wording wins in prose)
 * but still lets through PURE automation insertions — lines B added while keeping all of O's — so an
 * inserted line adjacent to a user edit isn't lost the way a wholesale conflict resolution would.
 *
 * Segment count is part of the "unchanged" test: an inserted blank line has empty text and would
 * otherwise sliceText-compare equal to an empty region, silently dropping the user's spacing
 * (GitHub #401). Length parity distinguishes a real no-op from a blank-line insertion.
 */
const resolveRegion = (oSlice: Array<Segment>, aSlice: Array<Segment>, bSlice: Array<Segment>): Array<Segment> => {
	const oText = sliceText(oSlice);
	if (sliceText(aSlice) === oText && aSlice.length === oSlice.length) return bSlice;
	if (sliceText(bSlice) === oText && bSlice.length === oSlice.length) return aSlice;
	if (sliceText(aSlice) === sliceText(bSlice) && aSlice.length === bSlice.length) return aSlice;

	const bTexts = new Set(bSlice.map((segment) => segment.text));
	const bIsPureInsertion = oSlice.every((segment) => bTexts.has(segment.text));
	if (!bIsPureInsertion) return aSlice;

	const kept = new Set([...oSlice, ...aSlice].map((segment) => segment.text));
	const inserted = bSlice.filter((segment) => !kept.has(segment.text));
	return [...aSlice, ...inserted];
};

/** Standard three-way (diff3) line merge: O = base, A = manual, B = generated. Anchors are lines unchanged by both sides; the gaps between them are resolved segment-by-segment. */
const diff3 = (oSegs: Array<Segment>, aSegs: Array<Segment>, bSegs: Array<Segment>): Array<Segment> => {
	const oText = oSegs.map((segment) => segment.text);
	const oToA = alignmentMap(oText, aSegs.map((segment) => segment.text));
	const oToB = alignmentMap(oText, bSegs.map((segment) => segment.text));

	const anchors: Array<[number, number, number]> = [];
	for (let o = 0; o < oSegs.length; o++) {
		if (oToA.has(o) && oToB.has(o)) anchors.push([o, oToA.get(o) as number, oToB.get(o) as number]);
	}
	anchors.push([oSegs.length, aSegs.length, bSegs.length]);

	const out: Array<Segment> = [];
	let prevO = -1;
	let prevA = -1;
	let prevB = -1;
	anchors.forEach(([oi, ai, bi]) => {
		out.push(...resolveRegion(oSegs.slice(prevO + 1, oi), aSegs.slice(prevA + 1, ai), bSegs.slice(prevB + 1, bi)));
		if (oi < oSegs.length) out.push(aSegs[ai]);
		prevO = oi;
		prevA = ai;
		prevB = bi;
	});
	return out;
};

/**
 * Prose reconciliation. A single-value (scalar) field keeps the user's explicit value and only lets
 * automation through when they never diverged from the base. Multi-line prose is a real diff3 merge
 * so model-driven line changes flow through the paragraphs the user left alone. With no base (legacy
 * override) there is nothing to merge against, so the manual value is kept unchanged — no regression.
 */
const reconcileProse = (params: ReconcileOverrideParams): string => {
	const { generated, manual, base } = params;
	if (base === undefined) return manual;

	const gen = splitSegments(generated);
	const man = splitSegments(manual);
	const bse = splitSegments(base);

	if (gen.length <= 1 && man.length <= 1 && bse.length <= 1) {
		return (man[0]?.text ?? '') === (bse[0]?.text ?? '') ? generated : manual;
	}

	return joinSegments(diff3(bse, man, gen));
};

// Model-first: the whole field is one atomic value, kept only while base still matches the live generated content — reclaimed by automation the instant it doesn't (no base included), since keyOf's whitespace rule can't key these fields' multi-word labels (`Fuel Source:`, `Non-Standard Changes:`, `Pg. N:`).
const reconcileDerived = (params: ReconcileOverrideParams): string => {
	const { generated, manual, base } = params;
	if (base === undefined) return generated;
	return stripText(base) === stripText(generated) ? manual : generated;
};

// Historical fact: once an override is stored, it IS the field's value and nothing in the generated side can replace
// it. Fields whose generated content is time-varying (`new Date()`-driven revision-log dates) can't use `prose` or
// `derived` — both let the current day's generated value through whenever base matches manual (which is exactly how
// EditableText captures the base on the user's first keystroke), making the saved date silently become today the
// next day the sheet is viewed (GitHub #742).
const reconcileFrozen = (params: ReconcileOverrideParams): string => params.manual;

/**
 * Merges a field's manual override with its freshly generated content so automation and manual edits
 * coexist: model changes flow through untouched segments while the user's edits survive on the
 * segments they touched. `keyed` aligns rows by SKU token (the appliance list); `prose` runs a
 * three-way line merge (freeform text fields); `derived` reclaims a computed field outright once
 * the model has moved past the value it was edited against.
 */
export const reconcileOverride = (params: ReconcileOverrideParams): string => {
	if (params.strategy === 'keyed') return reconcileKeyed(params);
	if (params.strategy === 'derived') return reconcileDerived(params);
	if (params.strategy === 'frozen') return reconcileFrozen(params);
	return reconcileProse(params);
};
